import * as React from 'react';

/**
 * @hidden
 */
export const firefox = typeof window !== 'undefined' && /Firefox/.test(window.navigator.userAgent);

/**
 * Represents the `skip` and `take` configurations which are wrapped in the `Page` object.
 */
export interface Page {
  /**
   * The number of records that will be skipped.
   */
  skip: number;

  /**
   * The number of records that will be taken.
   */
  take: number;
}

/**
 * @hidden
 */
export interface DataItemWrapper {
  dataItem: any;
  rowType: any;
  level: number;
  expanded: boolean;
  dataIndex: number;
}

/**
 * @hidden
 */
export class RowHeightService {
  private offsets: Array<number> = [];
  private heights: Array<number> = [];

  constructor(private total: number = 0, rowHeight: number, detailRowHeight: number, data?: DataItemWrapper[]) {
    let agg = 0;
    for (let idx = 0; idx < total; idx++) {
      this.offsets.push(agg);
      const currHeight = data && data[idx].expanded && data[idx].rowType === 'data' ? detailRowHeight : rowHeight;
      agg += currHeight;
      this.heights.push(currHeight);
    }
  }

  public height(rowIndex: number): number {
    return this.heights[rowIndex];
  }

  public index(position: number): number {
    for (let i = 0; i < this.offsets.length; i++) {
      if (position === this.offsets[i]) {
        return i;
      }
      if (position < this.offsets[i]) {
        return i - 1;
      }
    }
    return this.total - 1;
  }

  public offset(rowIndex: number): number {
    return this.offsets[rowIndex];
  }

  public totalHeight(): number {
    const lastOffset = this.offsets[this.offsets.length - 1];
    const lastHeight = this.heights[this.heights.length - 1];
    return lastOffset + lastHeight;
  }
}

/**
 * @hidden
 */
export class VirtualScrollFixed {
  public table: HTMLTableElement | null = null;
  public containerHeight = 0;
  public topCacheCount = 0;
  public attendedSkip = 0;
  public propsSkip = 0;
  public total = 0;
  public scrollableVirtual = false;
  public realSkip = 0;
  public pageSize = 0;
  public PageChange: ((event: Page, syntheticEvent: React.SyntheticEvent<any>) => void) | null = null;
  public tableBodyRef: any = null;
  public fixedScroll = false;
  public askedSkip: number | undefined = undefined;
  public containerRef: any = null;
  public tableTransform: string = '';

  public rowHeightService?: RowHeightService;
  private scrollSyncing: boolean = false;
  private lastLoaded: number = 0;
  private firstLoaded: number = 0;
  private lastScrollTop: number = 0;
  private reactVersion: number = Number.parseFloat(React.version);

  public get container(): HTMLDivElement | null {
    return this.containerRef?.current || undefined;
  }

  constructor(_cached: boolean) {
    this.firstLoaded = this.pageSize;
    this.lastLoaded = this.realSkip + this.pageSize;

    this.scrollHandler = this.scrollHandler.bind(this);
  }

  translate(dY: number, forceSet?: boolean) {
    if (this.scrollableVirtual && this.table) {
      if (firefox || this.reactVersion <= 17 || forceSet) {
        this.table.style.transform = 'translateY(' + dY + 'px)';
      } else {
        this.tableTransform = 'translateY(' + dY + 'px)';
      }
    }
  }

  changePage(skip: number, e: React.SyntheticEvent<HTMLDivElement>): void {
    if (this.PageChange) {
      this.PageChange({ skip: Math.max(0, skip), take: this.pageSize }, e);
    }
  }

  reset() {
    this.scrollSyncing = true;
    if (this.fixedScroll) {
      return;
    }
    if (this.container) {
      this.container.scrollTop = 0;
    }
    this.translate(0, true);
  }

  scrollHandler(e: React.SyntheticEvent<HTMLDivElement>) {
    if (
      !this.scrollableVirtual ||
      !this.container ||
      !this.table ||
      !this.rowHeightService ||
      !this.containerRef?.current
    ) {
      return;
    }

    if (this.scrollSyncing) {
      this.scrollSyncing = false;
      return;
    }

    const scrollTop = this.container.scrollTop;
    const up = this.lastScrollTop >= scrollTop;
    const down = !up;
    this.lastScrollTop = scrollTop;

    let firstItemIndex = this.rowHeightService.index(scrollTop)!;
    let firstItemOffset = this.rowHeightService.offset(firstItemIndex);

    const { offsetHeight } = this.containerRef.current;

    const lastItemIndex = this.rowHeightService.index(scrollTop + offsetHeight)!;

    if (down && lastItemIndex >= this.lastLoaded && this.lastLoaded < this.total) {
      const overflow = firstItemIndex + this.pageSize - this.total;

      if (overflow > 0) {
        firstItemIndex = firstItemIndex - overflow;
        firstItemOffset = this.rowHeightService.offset(firstItemIndex);
      }

      this.firstLoaded = firstItemIndex;
      this.translate(firstItemOffset);

      const nextTake = this.firstLoaded + this.pageSize;
      this.lastLoaded = Math.min(nextTake, this.total);

      this.changePage(this.firstLoaded, e);
    } else if (up && firstItemIndex < this.firstLoaded) {
      const nonVisibleBuffer = Math.floor(this.pageSize * 0.3);

      this.firstLoaded = Math.max(firstItemIndex - nonVisibleBuffer, 0);
      this.translate(this.rowHeightService.offset(this.firstLoaded));

      this.lastLoaded = Math.min(this.firstLoaded + this.pageSize, this.total);
      this.changePage(this.firstLoaded, e);
    }
  }
}
